import type { FC, PropsWithChildren, RefObject } from 'react';
import React, {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { styled } from '@compiled/react';
import type { ExecutionResult } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import { defineMessages, FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import uuid from 'uuid/v4';

import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';

import {
	ADD_INLINE_COMMENT_EXPERIENCE,
	ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
	ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
	VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { startEditorSubExperience } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import {
	clearCommentDraft,
	isOptimisticSaveEnabledForTypeAndMode,
} from '@confluence/comment/entry-points/draftUtils.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { handleAgentMentionInComment } from '@confluence/agent-mention/entry-points/handleAgentMentionInComment.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-common/entry-points/InlineCommentFramework.ts';
import { CommentAuthor } from '@confluence/inline-comments-common/entry-points/CommentAuthor.ts';
import { useCurrentUserAvatarReady } from '@confluence/inline-comments-common/entry-points/useCurrentUserAvatarReady.ts';
import {
	parseError,
	getTranslatedError,
	isOutOfDateError,
	isHighlightError,
	isUnexpectedError,
	isAnnotationError,
	isContentNotFoundError,
	isCreateNotPermittedError,
	isInlineMarkerRefAlreadyExistsError,
	isViewNotPermittedError,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import {
	getOptimisticResponseForInlineComment,
	type CreateInlineCommentInteropOptions,
	useCreateInlineCommentMutation,
} from '@confluence/comments-data-manager/entry-points/useCreateInlineCommentMutation.ts';
import {
	type ContentRepresentation,
	type useCreateInlineCommentMutation$data,
} from '@confluence/comments-data-manager/entry-points/useCreateInlineCommentMutationTypes.ts';
import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { i18n as inlineCommentCommonI18n } from '@confluence/inline-comments-common/entry-points/i18n.ts';
import { constructStepForGql } from '@confluence/comments-util/entry-points/rendererAnnotations.ts';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass.ts';
import { useShouldCommentSidebarBeSticky } from '@confluence/inline-comments-hooks/entry-points/useShouldCommentSidebarBeSticky.ts';
import { useCommentSidebarOffset } from '@confluence/inline-comments-hooks/entry-points/useCommentSidebarOffset.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { clearCollabDraftTimestamp } from '@confluence/fabric-providers/entry-points/CollabDraftCacheManager.ts';
import { CommentType } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { fg } from '@confluence/feature-gating';
import { usePageContentId } from '@confluence/page-context';
import { useWindowSize } from '@confluence/dom-helpers/entry-points/useWindowSize.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useContentRefetchDispatch } from '@confluence/content-body/entry-points/ContentRefetchContext.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import {
	handleCommentsPanelNavigationWithViewChange,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { isRawContentNotFoundError } from '@confluence/comments-util/entry-points/objectCommentsErrorUtils.ts';
import { objectCommentsi18n } from '@confluence/comments-util/entry-points/objectCommentsi18n.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	useCommentsPanelActions,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useAgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/useAgentMentionFyiNudge.ts';
import { AgentMentionFyiNudgeAsync as AgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/AgentMentionFyiNudgeAsync.ts';
import { useOmniBarVisibilityActions } from '@confluence/omni-bar-api/entry-points/OmniBarVisibilityStore.ts';

import { DIRECT_COMMENT_DATA_FLOW_METRIC } from './perf.config.ts';
import { CommentSidebar } from './CommentSidebar.tsx';
import type { SelectionOptions } from './renderer/SelectionComponent.tsx';

export const CREATE_TIMEOUT: ExperienceTimeout.CREATE_COMMENT_PUBLISH =
	ExperienceTimeout.CREATE_COMMENT_PUBLISH;

const relativeFilePath = 'annotation-provider-inline-comments/CreateComment';

const i18n = defineMessages({
	unableToApplyTitle: {
		id: 'annotation-provider-inline-comments.create.error.unable.to.apply.title',
		defaultMessage: 'Something went wrong',
		description: 'The title of the flag shown to a user something went wrong',
	},
	unableToApplyDescription: {
		id: 'annotation-provider-inline-comments.create.error.unable.to.apply.description',
		defaultMessage: 'We failed to display your comment. To view it, refresh now.',
		description:
			'A message to inform the user we failed to show their newly created comment could not be shown (Comment is a noun)',
	},
	unableToApplyOptimistically: {
		id: 'annotation-provider-inline-comments.create.error.unable.to.apply.annotation.optimistically',
		defaultMessage:
			'We were unable to apply your comment to the document. Comment is not saved. Please refresh and try again.',
		description:
			'A message to inform the user we were unable to apply their comment to the document and the comment is not saved.',
	},
	refreshContentLink: {
		id: 'annotation-provider-inline-comments.refresh.content.link',
		defaultMessage: 'Refresh content',
		description: 'A link for the user to click to refresh the content',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `${token('space.150')} ${token('space.200')}`,
});

const styles = cssMap({
	commentEditorContainer: {
		paddingTop: token('space.150'),
	},
});

type CreateCommentProps = {
	pageId: string;
	annotationElement?: HTMLElement | null;
	selectionOptions?: SelectionOptions;
	onCreate: (annotationId: string, commentData?: any) => void;
	onCreateError?: (annotationId: string) => void;
	onClose?: () => void;
	isEditor?: boolean;
	isOpeningMediaCommentFromToolbar?: boolean;
	isOffline?: boolean;
};

const getNodeType = (element: HTMLElement, annotationId: string) => {
	const nodeWithAnnotationWrapper = element.querySelector(`[id="${annotationId}"]`);
	if (nodeWithAnnotationWrapper) {
		const firsChildren = nodeWithAnnotationWrapper.firstChild;
		if (firsChildren?.nodeType === Node.TEXT_NODE) {
			return 'text';
		}

		const targetNode = nodeWithAnnotationWrapper.querySelector('[data-node-type]');
		if (targetNode) {
			return (targetNode as HTMLElement).dataset.nodeType;
		}
	}

	return 'unknown';
};

// Exporting for testing cases
export const handleDraftAnnotationDeletion =
	(
		setWasDraftAnnotationRemoved: (isRemoved: boolean) => void,
		isSavingComment: RefObject<boolean>,
	) =>
	(mutationList: MutationRecord[], observer: MutationObserver): void => {
		for (const mutation of mutationList) {
			if (mutation.type === 'childList') {
				if (mutation.removedNodes.length > 0) {
					mutation.removedNodes.forEach((node) => {
						const nodeClassList = Array.from((node as HTMLElement).classList ?? []);

						// COMMENTS-4308 - If any part of the draft annotation was removed, check if the entirity of the draft
						// 				   was removed, if so, we need to disable the editor buttons as the comment will fail
						if (nodeClassList.includes('ak-editor-annotation-draft')) {
							if (
								typeof document !== 'undefined' &&
								document.querySelector('.ak-editor-annotation-draft') === null
							) {
								// Tell the editor that the draft annotation was removed, unless user already clicked "save"
								!isSavingComment.current && setWasDraftAnnotationRemoved(true);

								// Still log the draft annotation removal for debugging
								createInlineCommentCompoundExperience.initExperience.debugPoint(
									`CreateComment dialog - draft annotation removed`,
								);

								// Disconnect the observer since we no longer need it
								observer.disconnect();
							}
						}
					});
				}
			}
		}
	};

const CommentEditorContainer = ({ children }: PropsWithChildren<{}>) =>
	expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
		<Box xcss={styles.commentEditorContainer}>{children}</Box>
	) : (
		children
	);

const CreateCommentComponent: FC<CreateCommentProps & WithFlagsProps> = ({
	pageId,
	annotationElement,
	selectionOptions,
	onClose,
	onCreate,
	onCreateError,
	isEditor,
	isOpeningMediaCommentFromToolbar,
	flags,
	isOffline,
}) => {
	const started = useRef(false);
	const pageClass = usePageClass();
	const { edition, cloudId, userId, displayName, accessStatus } = useSessionData();
	const isGuest = accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;
	if (started.current === false) {
		// Just in case `edition` is not set on `createInlineCommentCompoundExperience.start()`,
		// which can occur if the experience was started from within the editor platform code.
		createInlineCommentCompoundExperience.addCommonAttributes({ edition: edition ?? '' });

		createInlineCommentCompoundExperience.draftToPublishExperience.start();

		started.current = true;
	}

	const initialisedSelection = useRef(false);
	if (
		selectionOptions &&
		selectionOptions?.createdFrom === 'EDITOR' &&
		initialisedSelection.current === false
	) {
		initialisedSelection.current = true;
		if (selectionOptions.inlineNodeTypes) {
			createInlineCommentCompoundExperience.addCommonAttributes({
				inlineNodeTypes: selectionOptions.inlineNodeTypes,
			});
		}
	}

	const { formatMessage } = useIntl();
	const pageMode = useGetPageMode();
	const lastContentFetchTime = useRef(1);
	const requestTimeoutHandle = useRef<ReturnType<typeof setTimeout>>();
	const sidebarEl = useRef<HTMLDivElement | null>(null);
	const draftAnnotationMutationObserverRef = useRef<MutationObserver>();
	const commentContainerElementRef = useRef<HTMLDivElement | null>(null);
	const { documentUpdated, publishedDocumentVersion, currentDocumentVersion } =
		useDocumentUpdateStatus();
	const { setRefetchContent } = useContentRefetchDispatch();

	const createdAnnotationId = useRef<string | null>(null);
	const createdCommentId = useRef<string | null>(null);
	const isSavingComment = useRef(false);

	const [shouldRefetch, setShouldRefetch] = useState(false);
	const [wasDraftAnnotationRemoved, setWasDraftAnnotationRemoved] = useState(false);

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const {
		showFyi,
		agent,
		onContentChange: onAgentMentionContentChange,
	} = useAgentMentionFyiNudge({
		cloudId,
		enabled: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
	});

	// Track whether the FYI component has ever been shown, so we only load the
	// chunk on first mention but keep it mounted for the collapse animation.
	const hasShownFyiRef = useRef(false);
	useEffect(() => {
		if (showFyi) {
			hasShownFyiRef.current = true;
		}
	}, [showFyi]);

	const handleContentChangeWithData = useCallback(
		(_hasChanges: boolean, adf?: DocNode) => {
			onAgentMentionContentChange(adf);
		},
		[onAgentMentionContentChange],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addUnresolvedInlineComment, removeUnresolvedInlineComment } = useInlineCommentsActions();
	// Check if avatar data is ready - this hook runs independently and checks cache/loading state
	const isAuthorReady = useCurrentUserAvatarReady();
	const { width: windowWidth } = useWindowSize();
	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();
	const { setCurrentView, setCurrentlySelectedThreadKey } = useCommentsPanelActions();

	const { isSticky } = useShouldCommentSidebarBeSticky({
		annotationElement,
		isEditor: !!isEditor,
	});

	const sidebarOffset = useCommentSidebarOffset({
		isEditor: Boolean(isEditor),
		annotationElement,
		windowWidth,
		isSticky,
	});

	// Hide OmniBar while this component is mounted (viewing inline comment)
	const { requestHideOmniBar, releaseHideOmniBar } = useOmniBarVisibilityActions();
	useLayoutEffect(() => {
		requestHideOmniBar();
		return () => releaseHideOmniBar();
	}, [requestHideOmniBar, releaseHideOmniBar]);

	const { createInlineCommentInterop } = useCreateInlineCommentMutation({ contentId: pageId });

	const { pageInfo, loading, error, refetch } = usePageInfo({
		fetchPolicy: 'cache-first',
		onError: (err) => {
			experienceTracker.stopOnError({
				name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				error: err,
			});
		},
	});

	useEffect(() => {
		// should only refresh on the Content Out of date `Refresh` CTA is clicked
		if (documentUpdated && shouldRefetch && refetch) {
			void refetch();
			setShouldRefetch(false);
			onClose && onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentUpdated, shouldRefetch]);

	useEffect(() => {
		const metadata = document.querySelector('meta[name=confluence-request-time]');

		if (metadata) {
			const fetchTime = Number(metadata.getAttribute('content'));
			lastContentFetchTime.current = isNaN(fetchTime) ? 1 : fetchTime;
		}

		if (pageMode !== PageMode.VIEW) {
			const editorTextArea = document.getElementById('ak-editor-textarea');
			draftAnnotationMutationObserverRef.current = new MutationObserver(
				handleDraftAnnotationDeletion(setWasDraftAnnotationRemoved, isSavingComment),
			);

			// We need to watch the whole editorTextArea because highlights could
			if (editorTextArea) {
				draftAnnotationMutationObserverRef.current.observe(editorTextArea, {
					childList: true,
					subtree: true,
				});
			}
		}

		return () => {
			if (pageMode !== PageMode.VIEW && draftAnnotationMutationObserverRef.current) {
				draftAnnotationMutationObserverRef.current.disconnect();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Check for highlight element on mount - fail experience if createComment renders without a highlight
	// This tracks the scenario where createComment re-renders on close of the comment box without a highlight
	useEffect(() => {
		if (!expValEquals('confluence_comment_enable_exp_create_rerenderer', 'isEnabled', true)) {
			return;
		}
		// Use a delay to allow the DOM to settle after any state changes
		const timeoutId = setTimeout(
			() => {
				const draftAnnotationElement = __getDraftAnnotationElement();

				// Fail experience if createComment renders without a highlight
				if (!draftAnnotationElement && !wasDraftAnnotationRemoved) {
					createInlineCommentCompoundExperience.initExperience.fail(
						new Error('CreateComment renders without highlight'),
					);
				}
			},
			expValEquals('confluence_comments_fix_annotation_element_timeout', 'isEnabled', true)
				? 1000
				: 100,
		);

		return () => clearTimeout(timeoutId);
		// Only run on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const saveAttemptedTracker = useRef<{
		totalSaves: number;
		failedSaves: number;
		lastPublishFailure?: Error;
		lastMutationSucceeded: boolean;
	}>({
		lastMutationSucceeded: false,
		totalSaves: 0,
		failedSaves: 0,
	});
	useEffect(() => {
		return () => {
			const isOptimisticCreateEnabled = isOptimisticSaveEnabledForTypeAndMode(
				isEditor ? 'edit-inline' : 'inline',
				'create',
			);
			// TODO: (COMMENTS-6792) The error handling code in this useEffect unmount callback can be removed
			// after optimistic create is shipped, along with saveAttemptedTracker and saveAttemptedCounts.
			// With optimistic create, each save attempt immediately succeeds/aborts/fails the experience, so we
			// do not need to track the save attempts and errors to complete the experience only after the user closes
			// CreateComment. This is a much simpler and cleaner control flow.
			// Some of the error handling code below has been copied into the onCreateFailure callback.
			if (!isOptimisticCreateEnabled) {
				// When optimistic create is enabled, we immediately close i.e. unmount CreateComment, so all this error
				// handling code is always no-op and thus can be skipped.
				const savesWithoutError =
					// hit, the experience will already have completed in the `handleSaveComment`.
					saveAttemptedTracker.current.totalSaves - saveAttemptedTracker.current.failedSaves;

				if (saveAttemptedTracker.current.failedSaves > 0) {
					createInlineCommentCompoundExperience.addCommonAttributes({
						// eslint-disable-next-line react-hooks/exhaustive-deps
						failedSaves: saveAttemptedTracker.current.failedSaves,
					});
				}

				if (savesWithoutError > 1) {
					// At the point this is dismounting -- we don't know for certain if the user intended for this to be discarded or not
					// so we hard fail the full experience (it's possible the dismount is because the second save has worked and
					// the UI is about to load the new comment)
					createInlineCommentCompoundExperience.addCommonAttributes({
						savesWithoutError,
					});
					const msg = 'User clicked save, but UI did not update following successful save';
					// we log a debug point and continue with the flow, since creation is not necessarily failing
					createInlineCommentCompoundExperience.draftToPublishExperience.debugPoint(msg);
				}

				if (saveAttemptedTracker.current.lastPublishFailure) {
					if (saveAttemptedTracker.current.lastMutationSucceeded) {
						// in the case there was a publish failure, but the last mutation succeeded -- we assume the dismount is
						// because the ui is about to load the new comment - in this case we only mark the experience as a soft
						// failure (we'll capture more details and fail in the final experience step)
						createInlineCommentCompoundExperience.draftToPublishExperience.softFail(
							// eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentionally using a ref value due to the indirect flow of comment updates
							saveAttemptedTracker.current.lastPublishFailure,
						);

						return;
					} else {
						// In this scenario -- the user attempted to save at least once, got a network error, and gave up on
						// trying to create the comment

						// For a subset of errors -- the user is presented with a human readable error message describing that
						// the comment creation failure is due to the content changing (e.g. the user has edited the page) and
						// that they need to refresh the page to apply the comment.  In this scenario we don't treat the comment
						// creation as a failure, but rather we want to mark the experience as aborted.
						const contentChangedErrorSubstrings = [
							'Invalid arguments provided for matchIndex and/or numMatches',
							'Failed to insert highlight, invalid originalSelection, numMatches, or matchIndex',
							'Selection was made against a stale version of the content',
							'Published Version out of date. Please provide correct published version.',
						];
						const isContentChangedError = contentChangedErrorSubstrings.some((substring) =>
							// eslint-disable-next-line react-hooks/exhaustive-deps
							saveAttemptedTracker.current.lastPublishFailure?.message.includes(substring),
						);
						if (isContentChangedError) {
							createInlineCommentCompoundExperience.draftToPublishExperience.abort({
								abortReason: 'Unable to apply annotation to document',
							});

							return;
						}

						// This is handled in the UI appropriately, and we should abort not fail
						const permissionErrorSubstring = 'Could not create content with type comment';
						const isPermissionError =
							saveAttemptedTracker.current.lastPublishFailure?.message.includes(
								permissionErrorSubstring,
							);

						if (isPermissionError) {
							createInlineCommentCompoundExperience.draftToPublishExperience.abort({
								abortReason: 'User does not have space permission to create comments',
							});
						}

						const isErrorContentNotFoundError = isRawContentNotFoundError(
							// eslint-disable-next-line react-hooks/exhaustive-deps
							saveAttemptedTracker.current.lastPublishFailure?.message ?? '',
						);
						if (isErrorContentNotFoundError) {
							createInlineCommentCompoundExperience.draftToPublishExperience.abort({
								abortReason: 'Content no longer available for commenting',
							});
							return;
						}

						createInlineCommentCompoundExperience.draftToPublishExperience.stopOnError(
							// eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentionally using a ref value due to the indirect flow of comment updates
							saveAttemptedTracker.current.lastPublishFailure,
						);

						return;
					}
				}

				if (saveAttemptedTracker.current.lastMutationSucceeded) {
					createInlineCommentCompoundExperience.draftToPublishExperience.complete();

					return;
				}
			}
			// TODO: (COMMENTS-6792) - END of error handling code to be removed.

			if (!isOptimisticCreateEnabled || !isSavingComment.current) {
				// In this case the user did not attempt to save the comment, and so we assume this was
				// an intentional discard of the comment.
				// So we abort the draftToPublishExperience here, along with any unfinished initExperience.
				// Note: if experiences are already succeeded/aborted/failed, this will be a no-op.
				const abortReason = 'Create Comment UI dismissed';
				createInlineCommentCompoundExperience.draftToPublishExperience.abort({ abortReason });
				createInlineCommentCompoundExperience.initExperience.abort({ abortReason });

				DIRECT_COMMENT_DATA_FLOW_METRIC.cancel();
			}
		};
		// Missing `isEditor` in the dependency array, which is ok because its
		// value never changes, and this won't depend on it after optimistic
		// create is shipped.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const saveAttemptedCounts = useRef<{
		total: number;
		failedSaves: number;
	}>({
		total: 0,
		failedSaves: 0,
	});

	const failPublishExperience = useCallback(
		(commentPublishError: Error, translatedErrorMsg: MessageDescriptor) => {
			// COMMENTS-1879 - Only log unexpected errors or annotation application error as failures
			if (isUnexpectedError(translatedErrorMsg)) {
				experienceTracker.stopOnError({
					name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					error: commentPublishError,
				});
			} else {
				markErrorAsHandled(commentPublishError);
			}
		},
		[experienceTracker],
	);

	const onCacheUpdateError = useCallback(
		(cacheError: Error) => {
			const { message } = parseError(cacheError);
			failPublishExperience(cacheError, getTranslatedError(message));
		},
		[failPublishExperience],
	);

	const onCacheMiss = useCallback(
		(_cacheMissError: Error) => {
			void flags.showWarningFlag({
				title: <FormattedMessage {...objectCommentsi18n.unableToLoadCommentsTitle} />,
				description: <FormattedMessage {...objectCommentsi18n.networkError} />,
			});
		},
		[flags],
	);

	const handleSaveCommentWithTimeout = (timeoutInMs: number, saveMutation: Promise<void>) => {
		const timeout = new Promise((_resolve, reject) => {
			requestTimeoutHandle.current = setTimeout(() => {
				experienceTracker.stopOnError({
					name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					error: new Error(`Request took longer than ${timeoutInMs}ms`),
				});

				return reject({ error: inlineCommentCommonI18n.createTimeout });
			}, timeoutInMs);
		});

		return Promise.race([timeout, saveMutation]).finally(() => {
			if (requestTimeoutHandle.current) {
				clearTimeout(requestTimeoutHandle.current);
			}
		});
	};

	const handleSaveComment = (adf: JSONDocNode, onSuccess: () => void) => {
		saveAttemptedTracker.current.totalSaves = saveAttemptedTracker.current.totalSaves + 1;
		saveAttemptedTracker.current.lastMutationSucceeded = false;
		saveAttemptedCounts.current.total = saveAttemptedCounts.current.total + 1;

		createdAnnotationId.current = null;
		createdCommentId.current = null;
		isSavingComment.current = true;

		if (!selectionOptions) {
			// We likely will never get here, but just in case
			return Promise.reject({ error: inlineCommentCommonI18n.unableToGenerateStep });
		}

		startEditorSubExperience({
			name: VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
			attributes: {
				pageId,
				pageMode,
				isEditor,
			},
		});
		DIRECT_COMMENT_DATA_FLOW_METRIC.start();
		DIRECT_COMMENT_DATA_FLOW_METRIC.mark('commentCreationStart');
		startEditorSubExperience({
			name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
			attributes: {
				timeout: CREATE_TIMEOUT,
				isGuest,
			},
		});

		if (pageMode === PageMode.VIEW && fg('confluence_frontend_view_inline_comment_debug_v2')) {
			createInlineCommentCompoundExperience.addCommonAttributes({
				currentDocumentVersion,
				publishedDocumentVersion,
				isPageOutdated: currentDocumentVersion !== publishedDocumentVersion,
			});
		}

		const { numMatches, matchIndex, originalSelection, createdFrom, step, targetNodeType } =
			selectionOptions;

		// Generate a random annotation ID for optimistic rendering. We pass it as
		// inlineMarkerRef so the server echoes it back, meaning no ID reconciliation is needed.
		const optimisticAnnotationId = uuid();

		const isOptimisticCreateEnabled = isOptimisticSaveEnabledForTypeAndMode(
			isEditor ? 'edit-inline' : 'inline',
			'create',
		);

		// eslint-disable-next-line confluence-feature-gating/inline-usage
		const variables = {
			input: {
				containerId: pageId,
				commentBody: {
					value: JSON.stringify(adf),
					representationFormat: 'ATLAS_DOC_FORMAT' as ContentRepresentation,
				},
				numMatches,
				matchIndex,
				originalSelection,
				createdFrom,
				lastFetchTimeMillis: lastContentFetchTime.current,
				// In VIEW mode, we send the currentDocumentVersion so that the backend can
				// correctly identify where the inline annotation should be applied on the Page.
				// p.s. currentDocumentVersion is not set in EDIT/LIVE mode.
				publishedVersion:
					pageMode === PageMode.VIEW &&
					currentDocumentVersion &&
					expValEquals('confluence_fe_create_inline_use_curr_doc_version', 'isEnabled', true)
						? currentDocumentVersion
						: publishedDocumentVersion,
				...(!!step && { step: constructStepForGql(step) }),
				// Pass the annotation ID so the server echoes it back, enabling optimistic rendering
				// without needing to reconcile IDs when the response arrives.
				...(isOptimisticCreateEnabled ? { inlineMarkerRef: optimisticAnnotationId } : {}),
			},
			pageId,
		};

		// Note -- this mixes data saving logic and UI application logic.
		// And when the UI logic fails, the result is that the user is left with
		// the ui looking like the comment failed to save.
		// This should be refactored to separate the two concerns, and on a UI application
		// failure, we should encourage users to refresh (and mark the experience as failed).
		let succeededPublish = false;

		const onCreateSuccess = (retVal: ExecutionResult<useCreateInlineCommentMutation$data>) => {
			saveAttemptedTracker.current.lastMutationSucceeded = true;
			const { data } = retVal;
			if (requestTimeoutHandle.current) {
				clearTimeout(requestTimeoutHandle.current);
			}

			succeededPublish = true;
			createInlineCommentCompoundExperience.draftToPublishExperience.publishSucceeded();

			// Invalidate the collab draft cache since adding a comment modifies the ADF content
			// (adds annotation marks). This ensures the editor will fetch fresh content with
			// comment annotations if the user clicks Edit after adding a comment.
			clearCollabDraftTimestamp(pageId);

			// Mark comment creation end for both cohorts
			DIRECT_COMMENT_DATA_FLOW_METRIC.mark('commentCreationEnd');
			if (!isOptimisticCreateEnabled) {
				// When optimistic create is enabled, commentDataLoadStart has been marked before GQL mutation started.
				DIRECT_COMMENT_DATA_FLOW_METRIC.mark('commentDataLoadStart');
			}

			const annotationId = data?.createInlineComment?.location?.inlineMarkerRef;
			const commentId = data?.createInlineComment?.id;

			if (!annotationId || !commentId) {
				throw new Error(
					`Comment was not saved correctly - either annotationId '${annotationId}' or commentId '${commentId}' were undefined`,
				);
			}

			createdAnnotationId.current = annotationId;
			createdCommentId.current = commentId;

			// Reset the content changing flag
			resetContentChanged();

			// When not using optimistic rendering, apply the annotation and update the sidebar here.
			// When optimistic rendering is active, these were already applied immediately on submit,
			// so we skip them to avoid double-applying.
			if (!isOptimisticCreateEnabled) {
				// Grab the marker ref id returned and update the editor/renderer
				try {
					onCreate(annotationId, data?.createInlineComment);
				} catch (e) {
					// We generate errors in renderer, but this will also handle the editor case
					throw new Error(`Unable to apply annotation to document - ${e.message}`);
				}

				// Update the unresolved inline comments count
				addUnresolvedInlineComment(annotationId, pageMode);

				// For experiment cohort, we pass the mutation data to onCreate
				// The experiment success and data load completion will be handled in AnnotationsContext
				// after the view component renders the new data
			} else {
				// Mark draftToPublishExperience as successful after mutation succeeds and annotation is applied.
				createInlineCommentCompoundExperience.draftToPublishExperience.complete();
			}
			isSavingComment.current = false;

			// Create inline comment track event
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'created',
					actionSubject: 'comment',
					actionSubjectId: commentId,
					objectType: 'page',
					objectId: pageId,
					source: isEditor ? 'editPageScreen' : 'viewPageScreen',
					attributes: {
						source: AnalyticsSource.STANDALONE,
						commentType: CommentType.INLINE,
						parentCommentId: null,
						isReply: false,
						pageMode,
						isLivePage: createdFrom === 'LIVE',
						navdexPointType: END,
						inlineNodeTypes: selectionOptions.inlineNodeTypes,
						...(annotationElement &&
							!isEditor && {
								nodeType: getNodeType(annotationElement, annotationId),
							}),
						...(!isEditor && { targetNodeType }),
					},
				},
			}).fire();

			// Reset the editor (skipped when optimistic, since it was already reset on submit)
			if (!isOptimisticCreateEnabled) {
				onSuccess();

				// Code below are duplicated in handleSaveComment when isOptimisticCreateEnabled.
				// Not bothering to extract common code since this copy will be deleted after the
				// experiment is shipped.
				selectInlineCommentCompoundExperience.selectAnnotation.start({
					pageClass,
					commentType: 'inline',
					entryPoint: 'create-comment',
					annotationId,
				});

				/*
				 * On editor pages, the view-inline-comment experience is started by the editor when the
				 * created annotation is automatically selected after successful comment creation. For
				 * editor pages, we update the attributes here to ensure that the experience is tracked
				 * with the correct entry point.
				 */
				const attributes = {
					annotationId,
					entryPoint: relativeFilePath,
					pageClass,
				};

				if (pageClass === 'renderer') {
					experienceTracker.start({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
						attributes,
					});
				} else if (pageClass === 'editor') {
					experienceTracker.updateAttributes({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						attributes,
					});
				}

				if (isCommentsPanelShown) {
					experienceTracker.fail({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						error: new Error('Comments panel is visible when it should not be'),
						attributes: {
							stopPoint: relativeFilePath,
						},
					});
				}
			}
		};

		const onCreateFailure = (err: Error) => {
			saveAttemptedTracker.current.failedSaves = saveAttemptedTracker.current.failedSaves + 1;
			saveAttemptedCounts.current.failedSaves = saveAttemptedCounts.current.failedSaves + 1;

			experienceTracker.fail({
				name: VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
				error: err,
				attributes: {
					pageId,
					pageMode,
					isEditor,
					pointOfFailure: !succeededPublish ? 'running-mutation' : 'applying-comment-to-ui',
				},
			});
			DIRECT_COMMENT_DATA_FLOW_METRIC.cancel();

			createInlineCommentCompoundExperience.draftToPublishExperience.debug({
				error: err,
				createAnalyticsEvent,
				extraAttributes: {
					pointOfFailure: !succeededPublish ? 'running-mutation' : 'applying-comment-to-ui',
				},
			});

			if (!succeededPublish) {
				saveAttemptedTracker.current.lastPublishFailure = err;
			}

			isSavingComment.current = false;

			// Get a truncated graphql error message
			const { errorId, message } = parseError(err);
			const translatedError = getTranslatedError(message);

			if (isOptimisticCreateEnabled) {
				// Remove the optimistically added annotation.
				// A removal is required for EDIT/LIVE modes because in these
				// modes, the annotation is inserted into editor on frontend
				// and then synced to backend and saved into the document.
				// For VIEW mode, annotation is inserted into the page on
				// backend during the create-comment mutation, while the one
				// shown on frontend is temporary and local only, so simply
				// hiding it is sufficient when an error occurs.

				// `shouldPreserveOptimisticAnnotation` handles a special error
				// where the GQL mutation was retried due to a transient network
				// issue, and got this error because the previous mutation
				// actually succeeded and the comment was created in Backend
				// plus inserted into the Page if it's VIEW mode. In this case,
				// Frontend cannot display the new comment properly because the
				// full comment object wasn't returned by BE, but the new comment
				// will be there when user refreshes. So no special handling is
				// required other than showing user a warning flag, EXCEPT for
				// EDIT & LIVE modes, where FE should not remove the
				// optimistically inserted annotation for the new comment.
				const shouldPreserveOptimisticAnnotation =
					isInlineMarkerRefAlreadyExistsError(translatedError) &&
					pageMode !== PageMode.VIEW &&
					expValEquals('confluence_fe_create_inline_comment_marker_exists', 'isEnabled', true);
				if (!shouldPreserveOptimisticAnnotation) {
					try {
						onCreateError && onCreateError(optimisticAnnotationId);
					} catch (e) {
						// We log the error and let the rest of the error handling code run.
						createInlineCommentCompoundExperience.draftToPublishExperience.debug({
							error: new Error(
								`Unable to remove annotation from document on optimistic create failure - ${e.message}`,
							),
							createAnalyticsEvent,
						});
					}
				}

				removeUnresolvedInlineComment(optimisticAnnotationId);
			}

			let contentOutOfDate = false;

			if (
				isInlineMarkerRefAlreadyExistsError(translatedError) &&
				expValEquals('confluence_fe_create_inline_comment_marker_exists', 'isEnabled', true)
			) {
				markErrorAsHandled(err);
				createInlineCommentCompoundExperience.draftToPublishExperience.softFail(err);

				// Show a warning flag to user, informing them that the comment
				// was created but could not be displayed until they refresh.
				void flags.showWarningFlag({
					title: <FormattedMessage {...i18n.unableToApplyTitle} />,
					description: <FormattedMessage {...translatedError} />,
				});

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						objectType: 'page',
						objectId: pageId,
						action: 'alreadyCreated',
						actionSubject: 'inlineComment',
						actionSubjectId: 'inlineComment',
						attributes: {
							errorMessage: message,
							editor: 'FABRIC',
							framework: InlineCommentFramework.ANNOTATION_PROVIDER,
							isLivePage: pageMode === PageMode.LIVE,
							tenantId: cloudId,
							userId,
							pageId,
							annotationId: optimisticAnnotationId,
						},
					},
				}).fire();

				return;
			}

			// COMMENTS-1879 - The backend will fall back to the old method of trying to make string matches if the content
			// is out of date. If the BE fails to apply a comment when using the old fallback method we should log it
			if (isHighlightError(translatedError)) {
				// Send the analytics event
				const analyticsObject = {
					source: 'viewPage',
					objectType: 'page',
					objectId: pageId,
					action: 'invalid',
					actionSubject: 'highlight',
					actionSubjectId: 'inlineComment',
					attributes: {
						errorMessage: message,
						editor: 'FABRIC',
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						errorId,
						highlightOrigin: 'userHighlight', // TODO: Do we need to programmatically track this?
					},
				};

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: analyticsObject,
				}).fire();

				contentOutOfDate = true;
			}

			failPublishExperience(err, translatedError);

			// In the case of an annotation error, we should show a flag to the user to let them know
			// their comment has been saved and they need to refresh the content (view mode only)
			if (isAnnotationError(translatedError)) {
				const CREATE_ERROR_FLAG_ID = `create-error-${Date.now()}`;

				const getAnnotationErrorFlagActions = () => {
					if (pageMode === PageMode.VIEW) {
						// Only show the refetch content action on view mode as it's not applicable in editor/live
						return [
							{
								content: formatMessage(i18n.refreshContentLink),
								onClick: () => {
									setRefetchContent(true);
									handleOnClose();
									void flags.hideFlag(CREATE_ERROR_FLAG_ID);
								},
							},
						];
					} else {
						// pageMode === PageMode.EDITOR || PageMode.LIVE
						if (
							createdAnnotationId.current &&
							createdCommentId.current &&
							translatedError === inlineCommentCommonI18n.unableToApplyDanglingCommentAnnotation &&
							expValEquals('confluence_fe_show_created_dangling_comment_flag', 'isEnabled', true)
						) {
							return [
								{
									content: formatMessage(inlineCommentCommonI18n.goToCommentCTA),
									onClick: () => {
										const danglingView = expValEquals(
											'cc_comments_detached_in_open_view',
											'isEnabled',
											true,
										)
											? ViewValues.OPEN
											: ViewValues.DELETED_CONTENT;
										if (!isCommentsPanelShown) {
											showCommentsPanel({ openWithView: danglingView });
										} else {
											setCurrentView(danglingView);
										}
										setCurrentlySelectedThreadKey(createdAnnotationId.current!);

										void handleCommentsPanelNavigationWithViewChange({
											scrollBehavior: ScrollBehavior.SMOOTH,
											context: NavigationContext.PANEL,
											block: 'center',
											annotationId: createdAnnotationId.current!,
											commentId: createdCommentId.current!,
											commentType: 'INLINE',
											annotationClicked: true,
											expectedView: danglingView,
										});

										void flags.hideFlag(CREATE_ERROR_FLAG_ID);
									},
								},
							];
						}
						// TODO: Add more actions for editor/live if needed
					}
					return [];
				};

				void flags.showWarningFlag({
					id: CREATE_ERROR_FLAG_ID,
					title: <FormattedMessage {...i18n.unableToApplyTitle} />,
					description: (
						<FormattedMessage
							{...(translatedError === inlineCommentCommonI18n.unableToApplyAnnotation
								? i18n.unableToApplyDescription
								: translatedError)}
						/>
					),
					actions: getAnnotationErrorFlagActions(),
				});

				// Log the occurance through analytics so we can still track it
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						objectType: 'page',
						objectId: pageId,
						action: 'failed',
						actionSubject: 'applyAnnotation',
						actionSubjectId: 'inlineComment',
						attributes: {
							errorMessage: message,
							editor: 'FABRIC',
							framework: InlineCommentFramework.ANNOTATION_PROVIDER,
							isLivePage: pageMode === PageMode.LIVE,
							tenantId: cloudId,
							userId,
							pageId,
						},
					},
				}).fire();

				// Some code duplication.
				// Not extracting common code since `else` will be deleted after experiment is shipped.
				if (expValEquals('confluence_fe_show_created_dangling_comment_flag', 'isEnabled', true)) {
					createInlineCommentCompoundExperience.draftToPublishExperience.abort({
						abortReason: 'Unable to apply annotation to document',
						attributes: {
							detailedMessage: message,
							...(errorId ? { errorId } : {}),
							pageMode,
							context: isEditor ? 'editor' : 'renderer',
							pointOfFailure: !succeededPublish ? 'running-mutation' : 'applying-comment-to-ui',
							pageId,
						},
					});
				} else {
					createInlineCommentCompoundExperience.draftToPublishExperience.fail(
						new Error('Unable to apply annotation to document'),
						{
							detailedMessage: message,
							...(errorId ? { errorId } : {}),
							pageMode,
							context: isEditor ? 'editor' : 'renderer',
							pointOfFailure: !succeededPublish ? 'running-mutation' : 'applying-comment-to-ui',
							pageId,
						},
					);
				}

				onClose && onClose();

				return;
			}

			// For a subset of errors -- the user is presented with a human readable error message describing that
			// the comment creation failure is due to the content changing (e.g. the user has edited the page) and
			// that they need to refresh the page to apply the comment. In this scenario we don't treat the comment
			// creation as a failure, but rather we want to mark the experience as aborted.

			// These were copied from useEffect unmount callback. TODO: remove this comment after shipping experiment
			if (isOptimisticCreateEnabled) {
				const contentChangedErrorSubstrings = [
					'Invalid arguments provided for matchIndex and/or numMatches',
					'Failed to insert highlight, invalid originalSelection, numMatches, or matchIndex',
					'Selection was made against a stale version of the content',
					'Published Version out of date. Please provide correct published version.',
				];
				const isContentChangedError = contentChangedErrorSubstrings.some((substring) =>
					err.message.includes(substring),
				);
				if (isContentChangedError) {
					createInlineCommentCompoundExperience.draftToPublishExperience.abort({
						abortReason: 'Unable to apply annotation to document',
					});
				} else if (
					isCreateNotPermittedError(translatedError) ||
					isViewNotPermittedError(translatedError)
				) {
					createInlineCommentCompoundExperience.draftToPublishExperience.abort({
						abortReason: 'User does not have space permission to create comments',
					});
				} else if (isContentNotFoundError(translatedError)) {
					createInlineCommentCompoundExperience.draftToPublishExperience.abort({
						abortReason: 'Content no longer available for commenting',
					});
				} else {
					createInlineCommentCompoundExperience.draftToPublishExperience.stopOnError(err);
				}
			}

			// Use the existing value if it's already set or is an out of date error
			contentOutOfDate = contentOutOfDate || isOutOfDateError(translatedError);

			// CommentEditor expects a translated error message otherwise it fails
			// TODO: CommentEditor should likely just receive the translated message
			// rather than expect the object needed for FormattedMessage
			return Promise.reject({
				error: contentOutOfDate ? inlineCommentCommonI18n.contentOutOfDate : translatedError,
				contentOutOfDate,
			});
		};

		// Create the inline comment via the Comments Data Manager interop.
		const commentCreationInteropWrapper = (options: CreateInlineCommentInteropOptions) =>
			createInlineCommentInterop({
				options: {
					...options,
					// Show comment immediately before server response to reduce perceived latency.
					optimisticResponse: isOptimisticCreateEnabled
						? getOptimisticResponseForInlineComment({
								userId: userId ?? '',
								displayName: displayName ?? '',
								adf,
								pageId,
								annotationId: optimisticAnnotationId,
								inlineText: originalSelection,
							})
						: undefined,
				},
				onCacheUpdateError,
				onCacheMiss,
			});

		// When the experiment is enabled, apply the full UI immediately before the server responds:
		// - onCreate converts the draft highlight into a permanent annotation in the document
		// - onSuccess closes/resets the editor
		// The server is passed optimisticAnnotationId as inlineMarkerRef and echoes it back,
		// so onCreateSuccess skips these steps (they're already done).
		if (isOptimisticCreateEnabled) {
			DIRECT_COMMENT_DATA_FLOW_METRIC.mark('commentDataLoadStart');

			try {
				onCreate(optimisticAnnotationId);
			} catch (e) {
				// If optimistic apply annotation fails, we:
				// - remove any annotation that was added
				// - no need to send the GQL mutation; immediately abort the experience here
				// - let CommentEditor show an error flag to the user
				onCreateError && onCreateError(optimisticAnnotationId);

				DIRECT_COMMENT_DATA_FLOW_METRIC.cancel();
				createInlineCommentCompoundExperience.draftToPublishExperience.abort({
					abortReason: 'Unable to apply annotation to document',
					attributes: {
						detailedMessage: e.message,
						pageMode,
						context: isEditor ? 'editor' : 'renderer',
						pointOfFailure: 'applying-comment-to-ui-optimistic',
						pageId,
					},
				});

				return Promise.reject({ error: i18n.unableToApplyOptimistically }).finally(() => {
					isSavingComment.current = false;
				});
			}

			addUnresolvedInlineComment(optimisticAnnotationId, pageMode);

			onSuccess();

			selectInlineCommentCompoundExperience.selectAnnotation.start({
				pageClass,
				commentType: 'inline',
				entryPoint: 'create-comment',
				annotationId: optimisticAnnotationId,
			});

			const attributes = {
				annotationId: optimisticAnnotationId,
				entryPoint: relativeFilePath,
				pageClass,
			};

			if (pageClass === 'renderer') {
				experienceTracker.start({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
					attributes,
				});
			} else if (pageClass === 'editor') {
				experienceTracker.updateAttributes({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					attributes,
				});
			}

			if (isCommentsPanelShown) {
				experienceTracker.fail({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					error: new Error('Comments panel is visible when it should not be'),
					attributes: {
						stopPoint: relativeFilePath,
					},
				});
			}
		}

		const handleCreateSuccess = (retVal: ExecutionResult<useCreateInlineCommentMutation$data>) => {
			if (expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
				const commentId = retVal?.data?.createInlineComment?.id ?? '';
				handleAgentMentionInComment(adf, cloudId, pageId, commentId, userId ?? undefined, 'inline');
			}
			return onCreateSuccess(retVal);
		};

		const mutationPromise = commentCreationInteropWrapper({
			variables,
		});

		if (fg('confluence_frontend_add_inline_comment_timeout')) {
			return handleSaveCommentWithTimeout(
				CREATE_TIMEOUT,
				mutationPromise.then(handleCreateSuccess).catch(onCreateFailure),
			);
		} else {
			return mutationPromise.then(handleCreateSuccess).catch(onCreateFailure);
		}
	};

	const handleOnClose = useCallback(
		(hasContentChanged?: boolean) => {
			const reason = hasContentChanged
				? 'comment discarded by user'
				: 'empty comment discarded by user';

			experienceTracker.abort({
				name: ADD_INLINE_COMMENT_EXPERIENCE,
				reason,
			});

			// Draft is not cleared until a create-inline-comment success.
			// TODO: (COMMENTS-6792) Remove this entirely when optimistic create is rolled out.
			if (!isOptimisticSaveEnabledForTypeAndMode(isEditor ? 'edit-inline' : 'inline', 'create')) {
				// Clearing reply draft is handled in the comment editor
				clearCommentDraft(isEditor ? 'edit-inline' : 'inline', 'create');
			}

			onClose && onClose();
		},
		[experienceTracker, isEditor, onClose],
	);

	const [currentContentId] = usePageContentId();
	const initialContentIdRef = useRef(currentContentId);

	useEffect(() => {
		if (
			initialContentIdRef.current &&
			currentContentId &&
			initialContentIdRef.current !== currentContentId
		) {
			handleOnClose();
		}
	}, [currentContentId, handleOnClose]);

	const onEditorLoad = () => {
		experienceTracker.succeed({
			name: ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
		});
		const draftAnnotationElement = __getDraftAnnotationElement();

		if (!draftAnnotationElement && !wasDraftAnnotationRemoved) {
			// We have handling to show a message when we detect the draft annotation was removed, indicated by
			// `wasDraftAnnotationRemoved=true`. So we only need to fail the experience if we unexpectedly cannot find
			// the draftAnnotationElement, indicated by `wasDraftAnnotationRemoved=false`
			if (!expValEquals('confluence_comments_fix_annotation_element_timeout', 'isEnabled', true)) {
				createInlineCommentCompoundExperience.initExperience.fail(
					new Error('No annotation element'),
				);
			}

			return;
		}

		setTimeout(() => {
			// There are multiple useEffects involved in positioning the comment UI.
			// This works around this by only marking the experience as complete after
			// a short timeout + animation frame.
			requestAnimationFrame(() => {
				// In the Editor and Renderer, the comment UI is positioned relative to slightly different
				// elements.

				if (!draftAnnotationElement) {
					createInlineCommentCompoundExperience.initExperience.abort({
						abortReason: 'Draft annotation removed from document',
					});

					return;
				} else if (!sidebarEl.current) {
					createInlineCommentCompoundExperience.initExperience.fail(
						new Error('Missing create comment ui'),
					);
				} else {
					const commentUIVerticalDistanceFromAnnotation = Math.abs(
						draftAnnotationElement.getBoundingClientRect().top -
							sidebarEl.current.getBoundingClientRect?.().top,
					);

					// The 40 pixel allowance has been arbitrarily chosen to allow for some wiggle room
					if (commentUIVerticalDistanceFromAnnotation > 40) {
						const isVisible = sidebarEl.current.checkVisibility();
						if (isVisible) {
							// if it's fully visible, but misaligned, we don't treat this as a failure
							createInlineCommentCompoundExperience.initExperience.debug({
								error: new Error('View Created Comment UI too far from annotation'),
								createAnalyticsEvent,
								extraAttributes: { misalignedBy: commentUIVerticalDistanceFromAnnotation },
							});
							createInlineCommentCompoundExperience.initExperience.complete();
						} else {
							createInlineCommentCompoundExperience.addCommonAttributes({
								misalignedBy: commentUIVerticalDistanceFromAnnotation,
							});
							createInlineCommentCompoundExperience.initExperience.softFail(
								new Error('Draft Comment UI is setup too far from annotation'),
							);
						}
					} else {
						createInlineCommentCompoundExperience.initExperience.complete();
					}
				}
			});
		}, 1000);
	};

	if (loading || error) {
		createInlineCommentCompoundExperience.initExperience.debugPoint(
			`CreateComment dialog ${loading ? 'loading' : 'error'}`,
			{
				error: error?.message ?? '',
				loading,
			},
		);
		return null;
	}

	const spaceId = pageInfo?.space?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	const spaceOperations = fg('confluence_comments_space_operations_permissions')
		? pageInfo?.space?.operations || []
		: [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment =
		operations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment') ||
		spaceOperations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment');

	const hasMediaUploadPermissions = canAddComment && canAddAttachment;

	// Don't render CreateComment at all until author data is ready
	// This ensures both CommentAuthor and CommentEditor render together with data ready
	if (
		!isAuthorReady &&
		expValEquals('confluence_comment_improve_author_paint', 'isEnabled', true)
	) {
		return null;
	}

	return (
		<CommentSidebar
			pageId={pageId}
			onClose={handleOnClose}
			isEditor={isEditor}
			annotationElement={annotationElement}
			sidebarOffset={sidebarOffset}
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			sidebarEl={sidebarEl}
			isSticky={isSticky}
			isFabricPage
		>
			<Container ref={commentContainerElementRef}>
				<CommentAuthor
					size={expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? 'small' : 'medium'}
					commentMode="create"
					userId={userId}
				/>
				<CommentEditorContainer>
					{(showFyi || hasShownFyiRef.current) &&
						expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) && (
							<AgentMentionFyiNudge visible={showFyi} agent={agent} />
						)}
					<CommentEditor
						pageId={pageId}
						pageType={pageType}
						spaceId={spaceId}
						appearance="chromeless"
						EditorComponent={ChromelessEditor}
						onSaveComment={handleSaveComment}
						onContentChange={onChange}
						onContentChangeWithData={handleContentChangeWithData}
						commentMode="create"
						commentType={isEditor ? 'edit-inline' : 'inline'}
						onEditorReady={onEditorLoad}
						hideWatchCheckbox
						pageMode={isEditor ? 'edit' : 'view'}
						shouldWarnOnInternalNavigation
						useNewWarningModal
						setShouldRefetch={() => setShouldRefetch(true)}
						hasMediaUploadPermissions={hasMediaUploadPermissions}
						commentContainerElementRef={commentContainerElementRef}
						wasDraftAnnotationRemoved={wasDraftAnnotationRemoved}
						isStandaloneComment
						isOffline={isOffline}
					/>
				</CommentEditorContainer>
			</Container>
		</CommentSidebar>
	);
};

export const CreateComment: React.ComponentType<Omit<CreateCommentProps, 'flags'>> =
	withFlags(CreateCommentComponent);

/**
 *
 * Exported for testing purposes only
 *
 * This function is used to find the draft annotation element in the DOM.
 * It checks both the renderer and editor for the draft annotation element.
 *
 * In some scenarios, there are duplicate draft annotations in the DOM such
 * as in the renderer where the dom for table headers is repeated.
 *
 * To mitigate this, we get all possible draft annotation elements and
 * and return the first one that has a height greater than 0.
 *
 * If no draft annotation element is found, it returns null.
 */
export function __getDraftAnnotationElement(): HTMLElement | null {
	if (typeof document === 'undefined') {
		return null;
	}
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const possibleAnnotations = document.querySelectorAll(
		fg('confluence_fe_create_inline_comment_exp_coverage_2')
			? '[data-annotation-draft-mark="true"], .ak-editor-annotation-draft, .ak-editor-block-annotation-draft'
			: '[data-annotation-draft-mark="true"], .ak-editor-annotation-draft',
	);

	if (possibleAnnotations.length === 1) {
		// In cases where there is only one draft annotation, we can return it directly
		// and avoid the performance hit where reflow is triggered by checking if the
		// annotation has a height greater than 0.
		return possibleAnnotations[0] as HTMLElement;
	}

	for (let i = 0; i < possibleAnnotations.length; i++) {
		if (possibleAnnotations[i].getBoundingClientRect().height > 0) {
			return possibleAnnotations[i] as HTMLElement;
		}
	}

	return null;
}
